import { z } from 'zod';
import { ZPlatform } from './platform.js';
const ZOrderTaxLine = z.object({
    title: z.string().optional(),
    amount: z.number(),
    rate: z.number(),
});
const ZOrderDiscount = z.object({
    code: z.string().optional(),
    type: z.string(),
    amount: z.number(),
    target: z.string().optional(),
});
const ZOrderShippingMethod = z.object({
    id: z.string(),
    title: z.string(),
    amount: z.number(),
    currency: z.string().max(3),
    taxLine: z.union([ZOrderTaxLine.array(), ZOrderTaxLine]).optional(),
});
const ZOrderDuties = z.object({
    amount: z.number(),
    taxLines: ZOrderTaxLine.array().optional(),
});
const ZOrderLineItem = z.object({
    title: z.string(),
    name: z.string(),
    productId: z.string().optional(),
    sku: z.string().optional(),
    category: z.string().optional(),
    brand: z.string().optional(),
    requiresShipping: z.boolean().default(true),
    amount: z.number(),
    quantity: z.number().int().positive(),
    variantId: z.string().optional(),
    variantTitle: z.string().optional(),
    properties: z
        .object({ name: z.string(), value: z.string(), type: z.enum(['text', 'image']).optional() })
        .array()
        .optional(),
    taxLines: ZOrderTaxLine.array().optional(),
    duties: ZOrderDuties.array().optional(),
    taxable: z.boolean().optional(),
    imageUrl: z.string().optional(),
    discounts: ZOrderDiscount.array().optional(),
});
export const ZOrderPaymentServiceProvider = z.enum(['stripe']);
export const ZOrderStatus = z.enum([
    'unknown',
    'cancelled',
    'pending',
    'done',
    'validated',
    'payment_failed',
    'refunded',
    'partially_refunded',
    'capturable',
    'captured',
]);
export const ZOrderPaymentStatus = z.enum([
    'authorized',
    'pending',
    'paid',
    'partiallyPaid',
    'refunded',
    'voided',
    'partiallyRefunded',
    'unpaid',
]);
const ZOrderRefund = z.object({
    date: z.date(),
    amount: z.number(),
});
const ZOrderBillingAddress = z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().optional(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    zip: z.string(),
    province: z.string().optional(),
    provinceCode: z.string().optional(),
    country: z.string(),
    countryCode: z.string().optional(),
});
const ZOrderShippingAddress = z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().optional(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    zip: z.string(),
    province: z.string().optional(),
    provinceCode: z.string().optional(),
    country: z.string(),
    countryCode: z.string().optional(),
});
const ZOrderPaymentSource = z.object({
    id: z.string(),
    userId: z.string(),
    name: z.string(),
    type: z.string(),
    isDefault: z.boolean(),
    brand: z.string(),
    funding: z.string().optional(),
    country: z.string(),
    last4: z.number().int().max(9999),
    expMonth: z.number().int().max(12).positive(),
    expYear: z.number().int().positive(),
});
export const ZPlatformOrder = z.object({
    platform: ZPlatform,
    platformId: z.string(),
    cartId: z.string(),
    status: ZOrderStatus,
    email: z.string().email(),
    phone: z.string().max(32).optional(),
    billingAddress: ZOrderBillingAddress,
    shippingAddress: ZOrderShippingAddress,
    shippingMethod: ZOrderShippingMethod,
    discounts: ZOrderDiscount.array(),
    lineItems: ZOrderLineItem.array(),
    paymentSource: ZOrderPaymentSource.optional(),
    totalAmount: z.number(),
    conversionRate: z.number().optional(),
    totalWeight: z.number().optional(),
    currency: z.string().max(3),
    refunds: ZOrderRefund.array().optional(),
    totalRefunded: z.number().optional(),
    totalTax: z.number(),
    duties: ZOrderDuties.array().optional(),
    totalDuties: z.number().optional(),
    taxIncluded: z.boolean(),
    taxLines: ZOrderTaxLine.array(),
    paymentStatus: ZOrderPaymentStatus,
    note: z.string().optional(),
    url: z.string().optional(),
    name: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    /** Marketing attribution data collected from the merchant's website (click IDs, UTMs, cookies) */
    analytics: z.record(z.string(), z.string()).optional(),
});
const ZConvertedAmount = z.object({
    amount: z.number(),
    conversionFee: z.number(),
    conversionRate: z.number().optional(),
    net: z.number(),
    currency: z.string(),
});
export const ZOrder = ZPlatformOrder.extend({
    id: z.string().uuid(),
    platformId: z.string(),
    platformName: z.string().optional(),
    shopId: z.string(),
    customerId: z.string(),
    intentId: z.string(),
    psp: ZOrderPaymentServiceProvider,
    payoutId: z.string().optional(),
    requestId: z.string(),
    cartId: z.string().uuid().optional(),
    name: z.string(),
    fee: z.number(),
    createdAt: z.date(),
    updatedAt: z.date(),
    properties: z.object({ name: z.string(), value: z.string() }).array().optional(),
    convertedAmounts: z
        .object({
        fees: ZConvertedAmount,
        charges: ZConvertedAmount,
    })
        .optional(),
    version: z.number().optional(),
});
//# sourceMappingURL=order.js.map